<template>
  <div>
    <page-content-toolbar
      :projectName="`${project && project.name ? project.name : ' '}`"
      :title="$t('global.sourcesAndDeployments')"
    />
    <v-container fluid class="px-12 pt-0" >
      <div class="ma-8">
        <v-data-table
          :headers="headers"
          :items="sources"
          :loading="loadingSources"
          :options.sync="options"
          :server-items-length="totalSourcesAndDeployments"
          :footer-props="footerProps"
          class="elevation-1 rai-projects-table"
        >
          <template v-slot:item.lastModifiedDate="{ item }">
            {{ new Date(item.lastModifiedDate).toLocaleString($i18n.locale) }}
          </template>

          <template v-slot:item.deployment.lastModifiedDate="{ item }">
            {{ new Date(item.lastModifiedDate).toLocaleString($i18n.locale) }}
          </template>

          <template v-slot:item.stage="{ item }">
            {{ item.deployment ? item.deployment.stage : '' }}
          </template>

          <template v-slot:item.version="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  depressed
                  @click.stop="openSourceViewer(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ item.version }}
                </v-btn>
              </template>
              <span>{{ $t('sourcesAndDeployments.openSourceViewer') }}</span>
            </v-tooltip>
          </template>

          <template v-slot:item.status="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  depressed
                  @click.stop="openDeploymentDetails(item)"
                  :color="color(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                    {{ item.deployment ? $t('sourcesAndDeployments.status.' + item.deployment.status) : $t('sourcesAndDeployments.status.undefined')}}
                </v-btn>
              </template>
              <span>{{ $t('sourcesAndDeployments.openDeploymentDetails') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
        <div v-show="!loadingSources && !sources.length">
          {{ $t('sourcesAndDeployments.notFound') }}
        </div>
      </div>

      <v-row justify="center">
        <raison-button
          :button-label="$t('sourcesAndDeployments.generateSources')"
          :button-function="generateSources"
        >
          <template v-slot:append>
            <v-icon right>mdi-auto-fix</v-icon>
          </template>
        </raison-button>

        <raison-button
          :button-label="$t('sourcesAndDeployments.importSources')"
          :button-function="importSources"
        >
          <template v-slot:append>
            <v-icon right>mdi-import</v-icon>
          </template>
        </raison-button>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import PageContentToolbar from '@/components/layout/PageContentToolbar';
import RaisonButton from '@/components/global/RaisonButton';
import sourceService from '@/services/sources.js';
import deploymentService from '@/services/deployments.js';

import projectPageMixin from '@/mixins/project-page-mixin.js';
import userRightMixin from '@/mixins/user-right-mixin.js';

export default {
  name: 'SourcesAndDeployments',

  components: { PageContentToolbar, RaisonButton },

  mixins: [ projectPageMixin, userRightMixin ],

  data() {
    return {
      sources: [],
      totalSourcesAndDeployments: 2,
      loadingSources: false,
      options: {},
      firstLoad: true,
      sourceInterval: undefined,

    };
  },

  computed: {
    headers() {
      return [
        { text: this.$i18n.t('sourcesAndDeployments.sourceVersion'), value: 'version' },
        { text: this.$i18n.t('sourcesAndDeployments.deploymentStatus'), value: 'status' },
        { text: this.$i18n.t('sourcesAndDeployments.deploymentStage'), value: 'stage' },
        { text: this.$i18n.t('sourcesAndDeployments.sourceLastModifiedDate'), value: 'lastModifiedDate' },
        { text: this.$i18n.t('sourcesAndDeployments.deploymentLastModifiedDate'), value: 'deployment.lastModifiedDate' }
      ];
    },
    footerProps () {
      return {
        'items-per-page-options': [10, 20, 50, 100],
        'items-per-page-text': this.$i18n.t('global.rowPerPage'),
        'page-text': '{0}-{1} / {2}',
      };
    },
  },

  methods: {
    openSourceViewer(item) {
      // todo
      console.log('Click on ', item)
      this.$router.push({ name: 'sourceViewer', params: { id: this.$route.params.id, sourceId: item.id} });
    },
    openDeploymentDetails(item) {
      if (item.deployment) {
        this.$router.push({ name: 'deploymentDetails', params: { id: this.$route.params.id, deploymentId: item.deployment.id} });
      } else {
        this.$router.push({ name: 'newDeployment', params: { id: this.$route.params.id, sourceId: item.id} });
      }
    },
    generateSources() {
      this.$router.push({name: 'generateSource', params: { id: this.$route.params.id } });
    },

    importSources() {
      this.$router.push({name: 'uploadSource', params: { id: this.$route.params.id } });
    },

    color(item) {
      var status = item && item.deployment && item.deployment.status || undefined;
      var deploymentColor = deploymentService.statusColor(status);
      return deploymentColor;
    },

    listSources() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      return new Promise ((resolve, reject) => {
        this.loadingSources = true;
        const projectId = this.$route.params.id;
        sourceService.listSources(projectId, sortBy, sortDesc, page ? page - 1 : undefined, itemsPerPage).then(response => {
          if(response && response.data) {
            this.sources = response.data.content;
            this.totalSourcesAndDeployments = response.data.page.totalElements;
          }
          resolve();
        })
        .catch(error => {
          this.$emit('raison-event', { type: 'error', messages: [error] });
          reject();
        })
        .finally(() => {
          this.loadingSources = false;
        })
      })
    },
  },

  created() {
    if (!this.isUserParameterizer) {
      this.$router.push({name: 'dashboard'});
    }

    this.listSources();

    this.sourceInterval = window.setInterval(() => {
      this.listSources();
    }, 5000);
  },

  destroyed() {
    clearInterval(this.sourceInterval);
  },

  watch: {
    options: {
      handler () {
        if (this.firstLoad) {
          this.firstLoad = false;
        } else {
          this.listSources();
        }
      },
      deep: true,
    },
  },
};
</script>

<style>

</style>
